import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { getStatusChipToneStyles, StatusTone } from "theme/paletteUtils";
import { statusToColors, statusToTone } from "components/status-chip";

export { statusToTone };

const GATEWAY_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  authorized: { bg: "#90db70", fg: "#1a5c2a" },
  rejected: { bg: "#da7c7c", fg: "#fff" },
  pending: { bg: "#f5e7be", fg: "#7a5200" },
};

const MANUAL_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  new: { bg: "#ffface", fg: "#7a6a00" },
  completed: { bg: "#80ed99", fg: "#1a5c2a" },
  confirmed: { bg: "#d9ed92", fg: "#3a5a0a" },
  "pending payment": { bg: "#bad7f2", fg: "#0d3b6e" },
  paid: { bg: "#00a1e4", fg: "#fff" },
  "pending transfer": { bg: "#064789", fg: "#fff" },
  chargeback: { bg: "#f4a259", fg: "#6b3800" },
  refunded: { bg: "#ffd97d", fg: "#7a5200" },
  cancelled: { bg: "#bc4749", fg: "#fff" },
  declined: { bg: "#ee6055", fg: "#fff" },
  "declined by bank": { bg: "#ee6055", fg: "#fff" },
};

const chipSx = (bg: string, fg: string, size: "sm" | "md") => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  bgcolor: bg,
  color: fg,
  borderRadius: 999,
  px: size === "sm" ? "8px" : "10px",
  py: size === "sm" ? "3px" : "5px",
  whiteSpace: "nowrap" as const,
  maxWidth: "100%",
});

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

type Props = {
  status?: string | null;
  label?: string;
  gatewayStatus?: string | null;
  transactionType?: string | null;
  size?: "sm" | "md";
};

const TransactionStatus: React.FC<Props> = ({
  status,
  label,
  gatewayStatus,
  transactionType,
  size = "sm",
}) => {
  const theme = useTheme();
  if (!status && !label) return null;

  const displayLabel = label ?? status ?? "";
  const isManual = ["buy_manual", "sell_manual"].includes(
    String(transactionType || "").toLowerCase()
  );
  const manualColors = isManual
    ? MANUAL_STATUS_COLORS[String(status || "").toLowerCase()]
    : null;
  const exact = manualColors
    ? {
        ...manualColors,
        dot: manualColors.fg === "#fff" ? "#fff" : manualColors.fg,
      }
    : statusToColors(status);
  const tone: StatusTone = statusToTone(status);
  const { bg, fg, dot } = exact || getStatusChipToneStyles(theme, tone);

  const gatewayLabel = gatewayStatus
    ? String(gatewayStatus).toUpperCase()
    : null;
  const gatewayColors = gatewayLabel
    ? GATEWAY_STATUS_COLORS[gatewayLabel.toLowerCase()] || {
        bg: "#e8eef5",
        fg: "#334155",
      }
    : null;

  return (
    <Stack
      spacing={0.35}
      alignItems="flex-start"
      title={gatewayLabel ? `${displayLabel} · ${gatewayLabel}` : displayLabel}
    >
      {gatewayLabel && gatewayColors && (
        <Box sx={chipSx(gatewayColors.bg, gatewayColors.fg, size)}>
          <Dot
            color={gatewayColors.fg === "#fff" ? "#fff" : gatewayColors.fg}
          />
          <Typography
            component="span"
            variant={size === "sm" ? "statusLabel" : "statusLabelMd"}
          >
            {gatewayLabel}
          </Typography>
        </Box>
      )}
      <Box sx={chipSx(bg, fg, size)}>
        <Dot color={dot} />
        <Typography
          component="span"
          variant={size === "sm" ? "statusLabel" : "statusLabelMd"}
        >
          {displayLabel}
        </Typography>
      </Box>
    </Stack>
  );
};

export default TransactionStatus;
